import {MASTER_IFRAME_NAME} from '@/hosted_fields/common/connection/client';
import {strToHTML, has, isEqual} from '@/hosted_fields/common/dom-utils';
import {CardTypeIcon, CardIconSVG, CardIconInvalidSVG} from '@/hosted_fields/component/templates/card-templates';
import {Master as M, Field} from '@/hosted_fields/common/enums';
import {AllowedCardIcons} from '@/hosted_fields/common/types';
import {debounce} from '@/internal/common/utils';

type IconElement = SVGElement | HTMLImageElement;

export interface BinDataResponse {
  card_bin_info: Array<{
    brand: string;
    isPrepaid: boolean;
    cardType: string;
    countryName: string;
  }>;
  verification_info: {
    rule_id: string;
    verification_control: string;
    verification_control_outcome: Record<string, any>;
    verification_outcome_translation_key: string;
  };
}

const binDataCache: {[key: string]: BinDataResponse} = {};

export const substituteCardIcon = (
  els: IconElement[],
  iconImages: IconElement[],
  iconImageParent: HTMLElement,
  setSelectedScheme: (selectedScheme: string) => void,
  animateCardIcon: () => void
) => {
  clearIcons(iconImages);
  const isImageClickEventRequired = els.length > 1;

  els.forEach((el) => {
    iconImageParent.appendChild(el);
    if (isImageClickEventRequired) {
      el.addEventListener('click', () => {
        handleImageClick(iconImages, el, setSelectedScheme);
      });
    }
    iconImages.push(el);
  });
  animateCardIcon();
};

export const clearIcons = (iconImages: IconElement[]) => {
  iconImages.forEach((icon) => {
    if (icon.parentNode) {
      icon.parentNode.removeChild(icon);
    }
  });
  iconImages.length = 0;
};

export const handleImageClick = (
  iconImages: IconElement[],
  selectedImage: IconElement,
  setSelectedScheme: (scheme: string | undefined) => void
) => {
  iconImages.forEach((image) => {
    image.classList.remove('image-selected');
  });
  selectedImage.classList.add('image-selected');
  setSelectedScheme(selectedImage.getAttribute('data-card-type'));
};

export const createCardImage = (cardType: string): IconElement => {
  const cardImage = <HTMLImageElement>strToHTML(CardTypeIcon(cardType));
  cardImage.setAttribute('data-card-type', cardType);
  return cardImage;
};

export const createDefaultCardIcon = (): IconElement => {
  return <SVGElement>strToHTML(CardIconSVG);
};

export const createInvalidCardIcon = (): IconElement => {
  return <SVGElement>strToHTML(CardIconInvalidSVG);
};

export const isCardTypeAllowed = (cardType: string): boolean => {
  return has(AllowedCardIcons, cardType);
};

export const shouldUpdateCardIconAndScheme = (gwConfig: any, cardType: string): boolean => {
  const isValidCardType = ['visa', 'mastercard'].includes(cardType);
  return (
    gwConfig &&
    gwConfig.hasOwnProperty('supported_local_cards') &&
    Object.keys(gwConfig.supported_local_cards).length > 0 &&
    isValidCardType
  );
};

export const fetchBinData = (num: string, client: any): Promise<BinDataResponse> => {
  // Return cached response if available
  if (binDataCache[num]) {
    return Promise.resolve(binDataCache[num]);
  }

  const message = {
    action: M.Actions.GetBinData,
    data: {cardNumber: num},
  };

  return client.sendMessage(message, MASTER_IFRAME_NAME).then((response: BinDataResponse) => {
    // Cache the response before returning
    binDataCache[num] = response;
    return response;
  });
};

export const updateCardIconsAndScheme = (
  binData: BinDataResponse,
  cardImages: IconElement[],
  iconImages: IconElement[],
  iconImageParent: HTMLElement,
  gwConfig: any,
  setSelectedScheme: (scheme: string | undefined) => void,
  animateCardIcon: () => void
): void => {
  if (binData && binData.card_bin_info.length > 0) {
    const brand = binData.card_bin_info[0].brand;
    if (isCardTypeAllowed(brand)) {
      const cardImage = createCardImage(brand);
      if (gwConfig.supported_local_cards[brand] && gwConfig.supported_local_cards[brand].is_default) {
        cardImage.classList.add('image-selected');
        setSelectedScheme(brand);
      }
      cardImages.push(cardImage);
    }
  } else {
    setSelectedScheme(undefined);
  }
  substituteCardIcon(cardImages, iconImages, iconImageParent, setSelectedScheme, animateCardIcon);
};

export const originalHandleCardBinData = (
  num: string,
  client: any,
  cardImages: IconElement[],
  iconImages: IconElement[],
  iconImageParent: HTMLElement,
  gwConfig: any,
  cardType: any,
  setSelectedScheme: (scheme: string | undefined) => void,
  animateCardIcon: () => void,
  setBinData?: (binData: BinDataResponse | undefined) => void
): Promise<BinDataResponse> => {
  return fetchBinData(num, client).then((res: BinDataResponse) => {
    // Always update BIN data if callback is provided
    if (setBinData) {
      setBinData(res);
    }
    // Only update card icons if the card type is allowed
    if (shouldUpdateCardIconAndScheme(gwConfig, cardType)) {
      updateCardIconsAndScheme(
        res,
        cardImages,
        iconImages,
        iconImageParent,
        gwConfig,
        setSelectedScheme,
        animateCardIcon
      );
    }
    return res;
  });
};

const debouncedHandleCardBinData = debounce(originalHandleCardBinData, 300);

const handleCardBinData = (
  num: string,
  client: any,
  cardImages: IconElement[],
  iconImages: IconElement[],
  iconImageParent: HTMLElement,
  gwConfig: any,
  cardType: any,
  setSelectedScheme: (scheme: string | undefined) => void,
  animateCardIcon: () => void,
  setBinData?: (binData: BinDataResponse | undefined) => void
): void => {
  debouncedHandleCardBinData(
    num,
    client,
    cardImages,
    iconImages,
    iconImageParent,
    gwConfig,
    cardType,
    setSelectedScheme,
    animateCardIcon,
    setBinData
  );
};

export const isNumberFieldAndSameValue = (target: any, previousNumber) => {
  return target.id == Field.Number && previousNumber != target.value;
};

export const setCardIcon = (
  target: HTMLInputElement,
  isInvalid: boolean,
  cardNumber: string,
  cardType: any,
  prevCardType: string,
  prevInvalid: boolean,
  client: any,
  gwConfig: any,
  iconImageParent: HTMLElement,
  iconImages: IconElement[],
  setSelectedScheme: (scheme: string | undefined) => void,
  animateCardIcon: () => void,
  previousNumber: any,
  setBinData?: (binData: BinDataResponse | undefined) => void
) => {
  const cardImages: IconElement[] = [];
  if (!(isEqual(prevInvalid, isInvalid) && isEqual(prevCardType, cardType))) {
    if (!isInvalid) {
      if (cardType && isCardTypeAllowed(cardType)) {
        cardImages.push(createCardImage(cardType));
      } else {
        cardImages.push(createDefaultCardIcon());
      }
    } else {
      cardImages.push(createInvalidCardIcon());
    }
    substituteCardIcon(cardImages, iconImages, iconImageParent, setSelectedScheme, animateCardIcon);
  }

  if (target.id == Field.Number && cardNumber.length < 12) {
    setSelectedScheme(undefined);
    if (setBinData) {
      setBinData(undefined);
    }
  }

  if (cardNumber && cardNumber.length >= 12) {
    if (prevInvalid && !isInvalid) {
      handleCardBinData(
        cardNumber,
        client,
        cardImages,
        iconImages,
        iconImageParent,
        gwConfig,
        cardType,
        setSelectedScheme,
        animateCardIcon,
        setBinData
      );
    } else if (!isInvalid && isNumberFieldAndSameValue(target, previousNumber)) {
      if (cardImages.length == 0) {
        const cardImage = createCardImage(cardType);
        cardImages.push(cardImage);
      }
      handleCardBinData(
        cardNumber,
        client,
        cardImages,
        iconImages,
        iconImageParent,
        gwConfig,
        cardType,
        setSelectedScheme,
        animateCardIcon,
        setBinData
      );
    }
  }
};
